import { Icon, Position, Spinner } from '@blueprintjs/core';
import * as React from 'react';
import { SingleItemSelector } from 'vet-bones/components';

import { LabeledCard } from 'src/components/cards/LabeledCard';
import { DatabaseSelector } from 'src/components/DatabaseSelector';
import { TEST_IDS } from 'src/constants/testIds';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { isRefreshingDatabasesSelector } from 'src/selectors/isRefreshingDatabases';
import * as copy from 'src/templates/copy';
import { icons } from 'src/templates/icons';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export type DatabaseCardProps = {
  databaseId: string;
  disabled?: boolean;
  setDatabaseId: (id: string) => void;
};

export const DatabaseCard: React.VFC<DatabaseCardProps> = ({
  databaseId,
  disabled = false,
  setDatabaseId,
}) => {
  const isConnected = useReduxSelector(isConnectedSelector);
  const isRefreshingDatabases = useReduxSelector(isRefreshingDatabasesSelector);

  React.useEffect(() => {
    if (!isConnected) {
      setDatabaseId('');
    }
  }, [isConnected, setDatabaseId]);

  const isCardDisabled = !isConnected || isRefreshingDatabases || disabled;
  const databaseSelectorRef = React.useRef<SingleItemSelector>(null);
  let cardContent: JSX.Element;
  if (isRefreshingDatabases) {
    cardContent = <Spinner />;
  } else if (isConnected) {
    cardContent = (
      <DatabaseSelector
        databaseId={databaseId}
        disabled={disabled}
        popoverPosition={Position.TOP}
        selectorRef={databaseSelectorRef}
        setDatabaseId={setDatabaseId}
      />
    );
  } else {
    cardContent = <Icon icon={icons.database} />;
  }

  return (
    <LabeledCard
      disabled={isCardDisabled}
      label={copy.components.databaseSelector.placeholder}
      labelClassName="sd-database-card"
      onClick={() => {
        if (!isCardDisabled) {
          databaseSelectorRef.current?.setState?.({ isOpen: true });
        }
      }}
      testId={TEST_IDS.database.card}
    >
      {cardContent}
    </LabeledCard>
  );
};
